import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useNotificationStore } from '@/stores/notificationStore';
import { flightCostService, fmtCost } from '@/services/FlightCostService';

/** Organisation-wide fare-search spending limits (also editable by the agent on request). */
export function SpendingLimitsModal({ isOpen, onClose, onSaved }: { isOpen: boolean; onClose: () => void; onSaved?: () => void }) {
  const notify = useNotificationStore();
  const [monthly, setMonthly] = useState('');
  const [perRun, setPerRun] = useState('');
  const [reason, setReason] = useState('');
  const [usage, setUsage] = useState<{ spent_usd: number; runs: number; remaining_usd: number } | null>(null);
  const [meta, setMeta] = useState<{ updated_at: string | null; updated_by_name: string | null } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    Promise.all([flightCostService.settings(), flightCostService.usage()]).then(([s, u]) => {
      setMonthly(String(s.monthly_cap_usd)); setPerRun(String(s.per_run_cap_usd));
      setMeta({ updated_at: s.updated_at, updated_by_name: s.updated_by_name }); setUsage(u);
    }).catch((e) => notify.error(e?.message || 'Could not load spending limits'));
  }, [isOpen]);

  async function save() {
    const m = Number(monthly), p = Number(perRun);
    if (!(m > 0) || !(p > 0)) return notify.error('Limits must be positive amounts');
    if (p > m) return notify.error('The per-run limit cannot be higher than the monthly limit');
    if (!reason.trim()) return notify.error('Add a short reason for the change');
    setSaving(true);
    try {
      await flightCostService.updateSettings({ monthly_cap_usd: m, per_run_cap_usd: p, reason: reason.trim() });
      notify.success('Spending limits updated');
      onSaved?.(); onClose();
    } catch (e: any) { notify.error(e?.message || 'Could not update limits'); } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Spending limits" size="sm"
      description="Fare searches are paid to the fare provider. A run that would go over a limit will not start."
      footer={<div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} loading={saving}>Save</Button></div>}>
      <div className="space-y-3">
        {usage && (
          <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700">
            This month: <b>{fmtCost(usage.spent_usd)}</b> spent over {usage.runs} run{usage.runs === 1 ? '' : 's'} · {fmtCost(usage.remaining_usd)} left
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Input label="Monthly limit (USD)" type="number" min={1} value={monthly} onChange={(e) => setMonthly(e.target.value)} />
          <Input label="Per run limit (USD)" type="number" min={1} value={perRun} onChange={(e) => setPerRun(e.target.value)} />
        </div>
        <Input label="Reason" placeholder="e.g. Large program in March" value={reason} onChange={(e) => setReason(e.target.value)} />
        {meta?.updated_at && <p className="text-[11px] text-gray-500">Last changed {new Date(meta.updated_at).toLocaleDateString()}{meta.updated_by_name ? ` by ${meta.updated_by_name}` : ''}.</p>}
      </div>
    </Modal>
  );
}
